<template>
  <div class="error-nav-layout">
    <CRNavigation
      v-if="jobShop"
      v-append-parameters="urlParams"
      :branding-global="jobShop?.branding"
      :navigation-global="jobShop?.navigation"
      :social-media-global="jobShop?.['social-media']"
      :page-locale="jobShop?.locale"
      :job-shop-url="jobShop?.jobShopUrl"
      :menu-toggle-label="t('navigation.toggleMenu')"
      :main-nav-label="t('navigation.mainNav')"
      :mobile-nav-label="t('navigation.mobileNav')"
      :logo-alt-label="t('navigation.logoHome')"
      @emitted-click="(e) => trackNavigationClicked(e)"
    />

    <main class="error-nav-main">
      <div class="error-nav-content">
        <h1 class="error-nav-title">
          {{ t(titleKey) }}
        </h1>

        <p class="error-nav-text">
          {{ t(textKey) }}
        </p>

        <div class="error-nav-actions">
          <CRGlobalUICtaButton
            v-if="searchCtaButton"
            :cta-button="searchCtaButton"
          />
          <NuxtLink v-else class="error-nav-button" :to="searchPath">
            {{ t("error-navigation.search-link") }}
          </NuxtLink>

          <CRGlobalUICtaButton
            v-if="homeCtaButton"
            :cta-button="homeCtaButton"
          />
          <NuxtLink v-else class="error-nav-button error-nav-button--secondary" :to="homePath">
            {{ t("error-navigation.home-link") }}
          </NuxtLink>
        </div>
      </div>
    </main>

    <CRFooter
      v-if="jobShop"
      v-append-parameters="urlParams"
      :social-media-global="jobShop?.['social-media']"
      :navigation-global="jobShop?.navigation"
      :branding-global="jobShop?.branding"
      :footer-global="jobShop?.footer"
      :job-shop-url="jobShop?.jobShopUrl"
      :footer-logo-alt-label="t('navigation.footerLogoHome')"
      @cookie-settings-click="$cookieConsent.openCookieSettings()"
    />
  </div>
</template>

<script setup lang="ts">
import { useJobShopData } from "@/composables/useJobShopData"

// Shared full-page error layout with a headline, message, and navigation back
// into the JobShop. Used for the 410 "job gone" page and the 404 "not found"
// page (see error.vue). The caller passes i18n KEYS (not resolved strings) so
// the copy is translated here, after setLocale below has applied the jobshop's
// locale — otherwise the parent would resolve them in the default locale and the
// text would visibly switch languages once the locale is applied.
defineProps<{
  titleKey: string
  textKey: string
}>()

// Capture i18n helpers and other hooks before the await (async-setup constraint).
const { t, setLocale, locales } = useI18n()
const { $cookieConsent } = useNuxtApp()
const customPath = useCustomPath()
const urlParams = useQueryHeaders()

const jobShop = ref<JobShop | null>(null)

// Build JobShop-scoped links so navigation respects the custom path prefix
// (same pattern as pages/success.vue).
const homePath = computed(() => customPath.value || "/")
const searchPath = computed(() => `${customPath.value ?? ""}/search`)

// Build a CRGlobalUICtaButton config with our label/link and the given color
// treatment. The corner setting always follows the jobshop's navigation CTA
// config; returns null when that config is unavailable (see template fallback).
function toCtaButton(text: string, url: string, colors: { textColor: { value: string }, backgroundColor: { value: string }, fillStyle: { value: string } }) {
  const cta = jobShop.value?.navigation?.config?.ctaButton
  if (!cta)
    return null

  return {
    text,
    ...colors,
    corners: cta.corners,
    link: {
      type: { value: "external" },
      url,
      target: { value: "_self" },
    },
  }
}

// Primary action: inherit the jobshop's navigation CTA colors/fill.
const searchCtaButton = computed(() => {
  const cta = jobShop.value?.navigation?.config?.ctaButton
  return cta
    ? toCtaButton(t("error-navigation.search-link"), searchPath.value, {
      textColor: cta.textColor,
      backgroundColor: cta.backgroundColor,
      fillStyle: cta.fillStyle,
    })
    : null
})

// Secondary action: keep a neutral outline look, only the corner follows config.
const homeCtaButton = computed(() =>
  toCtaButton(t("error-navigation.home-link"), homePath.value, {
    textColor: { value: "#111" },
    backgroundColor: { value: "#E2E2E2" },
    fillStyle: { value: "border" },
  }),
)

// Fetch the JobShop config so the error page can be framed with the same
// navigation and footer as the rest of the site. The Nuxt error page renders in
// a fresh context, so the layout's cached "jobShopData" is not available here —
// we fetch it again (the header store is repopulated by the server plugin). On
// failure — e.g. an error raised before the JobShop resolved — we degrade to
// just the message and links.
try {
  const { jobShop: jobShopData } = await useJobShopData()
  jobShop.value = jobShopData.value

  // Drive the UI from the jobshop's locale, like the default layout does — the
  // error page renders in a fresh context that hasn't run that setLocale yet.
  if (jobShop.value)
    await setLocale(resolveI18nLocale(jobShop.value.locale, locales.value.map(l => l.code), "en"))
}
catch {
  // No JobShop context available; render without navigation/footer.
}
</script>

<style scoped lang="scss">
.error-nav-layout {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.error-nav-main {
  display: grid;
  place-content: center;

  // Give the message a prominent band between the nav and footer so it doesn't
  // look cramped when the footer is tall.
  min-height: 70dvh;
  padding: 40px 15px;
}

.error-nav-content {
  display: flex;
  flex-direction: column;
  gap: 25px;
  align-items: center;
  justify-content: center;

  max-width: 640px;

  text-align: center;
}

.error-nav-title {
  font-size: 46px;
  font-weight: 600;
  line-height: 1.17;
  color: #1D1D1F;
  letter-spacing: -0.03em;

  @media (width <= 733px) {
    font-size: 32px;
    letter-spacing: -0.0625em;
  }
}

.error-nav-text {
  font-size: 17px;
  font-weight: 600;
  line-height: 1.59;
  color: #7D7D7D;
}

.error-nav-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  align-items: center;
  justify-content: center;
}

.error-nav-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;

  height: 54px;
  padding: 0 30px;

  font-size: 17px;
  font-weight: 500;
  color: #fff;
  text-decoration: none;
  letter-spacing: 0.01em;

  background: #111;
  border: 2px solid #111;
  border-radius: 40px;

  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.75;
  }

  &--secondary {
    color: #111;
    background: transparent;
    border-color: #E2E2E2;
  }
}
</style>
